import { useState } from 'react'
import { useDispatch } from 'react-redux'
import { StoreDispatch } from '../../store/configure-store'
import { setUserMsg } from '../../store/user'
import { UserMsg } from '../../models/user'
import { httpService } from '../../services/http-service'
import { updateAuditControlMutation } from '../util-cmps/graphql-mutations/update-audit-control'
import { ConsolidationConfigBuilder, validateConsolidationPairings } from './consolidation-config-builder'
import { SystemTypes } from '../forms/is-required-system'
import { Backlog } from '../util-cmps/backlog'
import { Spinner } from '../util-cmps/spinner'
import { useEscapeKey } from '../../hooks/use-escape-key'
import { useScrollLock } from '../../hooks/use-scroll-lock'
import { useFocusTrap } from '../../hooks/use-focus-trap'
import { useDraggable } from '../../hooks/use-draggable'

interface BatchEditConsolidationModalProps {
    selectedAuditControls: any[]
    companyName: string
    onClose: () => void
    onSuccess: () => void
}

export const BatchEditConsolidationModal: React.FC<BatchEditConsolidationModalProps> = ({
    selectedAuditControls, companyName, onClose, onSuccess
}) => {
    const dispatch = useDispatch<StoreDispatch>()
    const [isSubmitting, setIsSubmitting] = useState(false)
    const [progress, setProgress] = useState<{ done: number; total: number } | null>(null)

    // Compare consolidation configs across selected ACs and pick the first non-empty one.
    // Normalize by parse + canonical re-stringify (sorted keys) so formatting and key-order
    // differences don't cause false mismatches.
    const consolidationComparison = (() => {
        const canonicalize = (val: any): any => {
            if (Array.isArray(val)) {
                // Recursively canonicalize each element, then sort the array by stringified form
                // so that order-of-rules differences don't trigger false mismatches.
                return val.map(canonicalize).sort((a, b) =>
                    JSON.stringify(a).localeCompare(JSON.stringify(b))
                )
            }
            if (val && typeof val === 'object') {
                return Object.keys(val).sort().reduce((acc: any, k) => {
                    acc[k] = canonicalize(val[k])
                    return acc
                }, {})
            }
            return val
        }
        const normalize = (val: any): { display: string; canonical: string } => {
            try {
                const parsed = typeof val === 'string' ? JSON.parse(val) : val
                return { display: JSON.stringify(parsed), canonical: JSON.stringify(canonicalize(parsed)) }
            } catch { return { display: '[]', canonical: '[]' } }
        }
        const configs = selectedAuditControls.map(ac => {
            const raw = ac.control?.systemConsolidations
            const { display, canonical } = raw ? normalize(raw) : { display: '[]', canonical: '[]' }
            return { acId: ac.id, json: display, canonical }
        })
        const nonEmpty = configs.filter(c => c.canonical !== '[]' && c.canonical !== 'null' && c.canonical !== '')
        const uniqueConfigs = Array.from(new Set(nonEmpty.map(c => c.canonical)))
        return { configs, nonEmpty, uniqueConfigs, allSame: uniqueConfigs.length <= 1 }
    })()

    const initialConfig = consolidationComparison.nonEmpty[0]?.json || '[]'
    const [consolidationJson, setConsolidationJson] = useState(initialConfig)

    useEscapeKey(onClose)
    useScrollLock()
    const trapRef = useFocusTrap<HTMLDivElement>()
    const { modalRef, onHeaderMouseDown } = useDraggable<HTMLElement>()

    // Compute union of system types across all selected ACs
    const allSystemTypes = Array.from(new Set(
        selectedAuditControls.flatMap(ac => {
            const types: string[] = []
            if (ac.control?.mainSystem?.id) types.push(SystemTypes.IN_SCOPE)
            if (ac.control?.hrSystem?.id) types.push(SystemTypes.HR)
            if (ac.control?.ticketSystem?.id) types.push(SystemTypes.TICKET)
            return types
        })
    ))

    const hasHr = allSystemTypes.includes(SystemTypes.HR)
    const hasTicket = allSystemTypes.includes(SystemTypes.TICKET)

    const onSubmit = async () => {
        // Validate
        const pairingError = validateConsolidationPairings(consolidationJson, hasHr, hasTicket)
        if (pairingError) {
            dispatch(setUserMsg({ msg: { type: 'danger', txt: pairingError } }))
            return
        }

        setIsSubmitting(true)
        const total = selectedAuditControls.length
        let done = 0
        let errors = 0
        setProgress({ done: 0, total })

        const query = updateAuditControlMutation()

        for (const ac of selectedAuditControls) {
            try {
                await httpService.graphql(companyName, {
                    query,
                    variables: {
                        auditControlId: Number(ac.id),
                        systemConsolidations: consolidationJson,
                    }
                })
                done++
            } catch {
                errors++
                done++
            }
            setProgress({ done, total })
        }

        setIsSubmitting(false)

        if (errors > 0) {
            const msg: UserMsg = { type: 'danger', txt: `Updated ${done - errors}/${total} controls. ${errors} failed.` }
            dispatch(setUserMsg({ msg }))
        } else {
            const msg: UserMsg = { type: 'success', txt: `Consolidation config updated for ${total} audit control${total !== 1 ? 's' : ''}` }
            dispatch(setUserMsg({ msg }))
            onSuccess()
        }
    }

    // List affected AC IDs for review
    const acIds = selectedAuditControls.map(ac => ac.id).join(', ')

    return <div ref={trapRef}>
        <section ref={modalRef} className="batch-edit-modal" role="dialog" aria-modal="true" aria-label="Batch Edit Consolidation">
            <header onMouseDown={onHeaderMouseDown}>
                <h2>Batch Edit Consolidation</h2>
                <button className="close-btn" onClick={onClose} aria-label="Close">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <line x1="18" y1="6" x2="6" y2="18" />
                        <line x1="6" y1="6" x2="18" y2="18" />
                    </svg>
                </button>
            </header>

            <section className="modal-form-body">
                <div className="form-section-card">
                    <div className="form-section-label">Scope</div>
                    <p className="form-info">
                        Updating <strong>{selectedAuditControls.length}</strong> audit control{selectedAuditControls.length !== 1 ? 's' : ''}: {acIds}
                    </p>
                    <p className="form-info">
                        System types: {allSystemTypes.join(', ')}
                    </p>
                    {consolidationComparison.allSame && consolidationComparison.nonEmpty.length > 0 && (
                        <p className="form-info form-info--success">
                            All selected controls share the same consolidation config (pre-filled below).
                        </p>
                    )}
                    {!consolidationComparison.allSame && (
                        <>
                            <p className="form-info form-info--warning">
                                Selected controls have {consolidationComparison.uniqueConfigs.length} different consolidation configs.
                                Pre-filled with config from AC #{consolidationComparison.nonEmpty[0]?.acId}.
                            </p>
                            <details className="distinct-configs">
                                <summary>Show distinct configs</summary>
                                {consolidationComparison.uniqueConfigs.map((canonical, i) => {
                                    const acIds = consolidationComparison.nonEmpty
                                        .filter(c => c.canonical === canonical)
                                        .map(c => c.acId)
                                    return (
                                        <div key={i} className="distinct-config-block">
                                            <div className="distinct-config-acs">AC #{acIds.join(', #')}:</div>
                                            <pre>{canonical}</pre>
                                        </div>
                                    )
                                })}
                            </details>
                        </>
                    )}
                    {consolidationComparison.nonEmpty.length === 0 && (
                        <p className="form-info">
                            None of the selected controls have a consolidation config set.
                        </p>
                    )}
                </div>

                <div className="form-section-card">
                    <div className="form-section-label">Consolidation Config</div>
                    <ConsolidationConfigBuilder
                        value={consolidationJson}
                        onChange={setConsolidationJson}
                        availableSystemTypes={allSystemTypes}
                    />
                </div>

                <div className="form-actions">
                    {progress && <span className="batch-progress">{progress.done}/{progress.total}</span>}
                    {!isSubmitting && <button className="main" onClick={onSubmit}>Update All</button>}
                    {isSubmitting && <span className="spinner"><Spinner isSmall={true} /></span>}
                </div>
            </section>
        </section>
        <Backlog zIndex={199} onCloseBacklog={onClose} />
    </div>
}
